import React, { useState, useEffect } from 'react';
import {
  Timeline,
  Space,
  Avatar,
  Typography,
  Empty,
  Spin,
  Tag,
  Button,
  Card,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  UserSwitchOutlined,
  FileAddOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ToolOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { getCaseMilestones, CaseMilestone } from '../../../../services/case';
import AddMilestoneModal from './AddMilestoneModal';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');
dayjs.tz.setDefault('Asia/Shanghai');

const { Text } = Typography;

interface CaseMilestoneTabProps {
  caseId: number;
  canEdit: boolean;
}

const CaseMilestoneTab: React.FC<CaseMilestoneTabProps> = ({ caseId, canEdit }) => {
  const [loading, setLoading] = useState(false);
  const [milestones, setMilestones] = useState<CaseMilestone[]>([]);
  const [modalVisible, setModalVisible] = useState(false);

  // Load milestones
  const loadMilestones = async () => {
    if (!caseId) return;

    setLoading(true);
    try {
      const data = await getCaseMilestones(caseId);
      // Sort by created_at descending (newest first)
      const sortedData = [...data].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setMilestones(sortedData);
    } catch (error) {
      console.error('Failed to load milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (caseId) {
      loadMilestones();
    }
  }, [caseId]);

  // Handle modal success
  const handleModalSuccess = () => {
    setModalVisible(false);
    loadMilestones();
  };

  // Get milestone type icon and color
  const getMilestoneStyle = (type: string) => {
    const isAutoGenerated = [
      '创建案件',
      '案件创建',
      '状态变更',
      '人员变更',
      '当事人变更',
      '案件详情变更',
      '协作人员变更',
    ].some(autoType => type.includes(autoType));

    const styleMap: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
      '创建案件': {
        icon: <CheckCircleOutlined />,
        color: '#52c41a',
        bgColor: '#f6ffed',
      },
      '案件创建': {
        icon: <CheckCircleOutlined />,
        color: '#52c41a',
        bgColor: '#f6ffed',
      },
      '状态变更': {
        icon: <EditOutlined />,
        color: '#1890ff',
        bgColor: '#e6f7ff',
      },
      '人员变更': {
        icon: <UserSwitchOutlined />,
        color: '#722ed1',
        bgColor: '#f9f0ff',
      },
      '当事人变更': {
        icon: <TeamOutlined />,
        color: '#fa8c16',
        bgColor: '#fff7e6',
      },
      '案件详情变更': {
        icon: <EditOutlined />,
        color: '#13c2c2',
        bgColor: '#e6fffb',
      },
      '协作人员变更': {
        icon: <TeamOutlined />,
        color: '#eb2f96',
        bgColor: '#fff0f6',
      },
      '开庭': {
        icon: <ClockCircleOutlined />,
        color: '#fa541c',
        bgColor: '#fff2e8',
      },
      '判决': {
        icon: <FileAddOutlined />,
        color: '#faad14',
        bgColor: '#fffbe6',
      },
    };

    // Check for specific types
    for (const [key, value] of Object.entries(styleMap)) {
      if (type.includes(key)) {
        return { ...value, isAutoGenerated };
      }
    }

    // Default style for manual entries
    return {
      icon: <ToolOutlined />,
      color: isAutoGenerated ? '#8c8c8c' : '#1890ff',
      bgColor: isAutoGenerated ? '#fafafa' : '#e6f7ff',
      isAutoGenerated,
    };
  };

  if (loading && milestones.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div className="case-milestone-tab">
      <Card>
        {/* Header with Add Button */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>案件大事记</Text>
            {milestones.length > 0 && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({milestones.length})
              </Text>
            )}
          </div>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              添加大事记
            </Button>
          )}
        </div>

        {/* Timeline */}
        {milestones.length === 0 ? (
          <Empty
            description="暂无大事记"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Timeline>
            {milestones.map((milestone) => {
              const style = getMilestoneStyle(milestone.milestone_type);

              return (
                <Timeline.Item
                  key={milestone.id}
                  dot={
                    <Avatar
                      size="small"
                      icon={style.icon}
                      style={{
                        backgroundColor: style.bgColor,
                        color: style.color,
                      }}
                    />
                  }
                  color={style.color}
                >
                  <div className={`timeline-item-content ${style.isAutoGenerated ? 'timeline-item-auto' : ''}`}>
                    <Space direction="vertical" size={4} style={{ width: '100%', maxWidth: '100%' }}>
                      <Space size="small" wrap style={{ maxWidth: '100%' }}>
                        <Text strong>{milestone.milestone_type}</Text>
                        {style.isAutoGenerated && (
                          <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                            自动生成
                          </Tag>
                        )}
                      </Space>
                      <div style={{ width: '100%', maxWidth: '100%' }}>
                        <Text
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            display: 'block',
                          }}
                        >
                          {milestone.description}
                        </Text>
                      </div>
                      <Space size="small" wrap>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          操作人: {milestone.operator_name || '系统'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ·
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs.utc(milestone.created_at).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm')}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ·
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs.utc(milestone.created_at).tz('Asia/Shanghai').fromNow()}
                        </Text>
                      </Space>
                    </Space>
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        )}
      </Card>

      {/* Add Milestone Modal */}
      <AddMilestoneModal
        visible={modalVisible}
        caseId={caseId}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default CaseMilestoneTab;
